import { ResponsiveLine } from "@nivo/line";
import { WatchTimeScene, WatchTimeOverallPoint } from "../Analytics";
import { TooltipLineNoFormat } from "./Tooltips";
import { theme } from "../../../theme";
import { Select } from "antd";
import { useEffect, useState } from "react";
const FRAME_RATE = 25;
const TIME_FRAME = 5;
const FRAME_PRECISION = FRAME_RATE * TIME_FRAME;
const numberFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  compactDisplay: "short",
});
export const WatchTime = ({
  watchTimesPerScene,
  watchTimesOverall,
}: {
  watchTimesPerScene?: Array<WatchTimeScene>;
  watchTimesOverall?: Array<WatchTimeOverallPoint>;
}) => {
  const watchTimes = watchTimesPerScene?.map((scene) => ({
    id: scene.sceneId,
    data: fillMissingData(
      scene.frames.map((d) => ({
        x: d.frameNumber / FRAME_RATE,
        y: d.viewerCount,
      }))
    ),
  }));
  useEffect(() => {
    if (!selectedScene) {
      setSelectedScene(watchTimes?.length ? watchTimes[0].id : selectedScene);
    }
  }, [watchTimes]);
  const [selectedScene, setSelectedScene] = useState(
    watchTimesOverall
      ? "TOTAL_WATCH_TIME"
      : watchTimes?.length
      ? watchTimes[0].id
      : ""
  );
  if (!watchTimes) return null;
  return (
    <div
      style={{ height: "fit-content" }}
      className="watch-time analytics-view"
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div className="view-title">
          <h2>Watch time</h2>
        </div>
        <Select
          onChange={(selected) => {
            setSelectedScene(selected);
          }}
          value={selectedScene}
          style={{ marginBottom: "1rem", width: "15rem" }}
          options={[
            ...(watchTimesOverall?.length
              ? [
                  {
                    value: "TOTAL_WATCH_TIME",
                    label: "Overall Video Viewing Time",
                  },
                ]
              : []),
            {
              label: "All scenes",
              options: watchTimes.map((wt) => ({
                value: wt.id,
                label: wt.id,
              })),
            },
          ]}
        >
          {watchTimes.map((wt) => (
            <Select.Option value={wt.id} key={wt.id}>
              {wt.id}
            </Select.Option>
          ))}
        </Select>
      </div>
      <div style={{ width: "100%", height: "350px" }}>
        {selectedScene === "TOTAL_WATCH_TIME" ? (
          <WatchTimeTotal data={watchTimesOverall} />
        ) : (
          <ResponsiveLine
            data={watchTimes.filter((wt) => wt.id === selectedScene)}
            pointColor={"#ff3666"}
            enableArea={true}
            curve={"monotoneX"}
            yScale={{ type: "linear", min: 0, max: "auto" }}
            axisBottom={{
              legend: "",
              tickValues: 3,
              tickRotation: -45,
              format: (d) => d + "s",
            }}
            xFormat={(d) => `${d.toString()}s`}
            axisLeft={{
              legend: "",
              legendOffset: 12,
              format: (e) => Math.floor(e) === e && numberFormatter.format(e),
            }}
            margin={{
              left: 60,
              right: 30,
              top: 30,
              bottom: 45,
            }}
            motionConfig="gentle"
            useMesh={true}
            colors={["#ff366669"]}
            fill={[{ match: "*", id: "gradientC" }]}
            theme={{
              background: "#F7F8FA",
              axis: { domain: { line: { stroke: "gray", strokeWidth: 1 } } },
              textColor: theme.blings_teal,
              grid: {
                line: { stroke: theme.blings_teal_80, strokeWidth: 0.1 },
              },
            }}
            tooltip={TooltipLineNoFormat}
          />
        )}
      </div>
    </div>
  );
};

function fillMissingData(watchTime: Array<{ x: number; y: number }>) {
  const existingTimes = watchTime.map((w) => w.x);
  const maxTime = Math.max(...existingTimes);
  const existingTimesSet = new Set(existingTimes);
  const missingTimes = [];
  for (let i = 0; i < maxTime; i += FRAME_PRECISION) {
    if (!existingTimesSet.has(i)) missingTimes.push({ x: i, y: 0 });
  }
  return [...watchTime, ...missingTimes].sort((a, b) => a.x - b.x);
}

const WatchTimeTotal = ({
  data,
}: {
  data?: Array<WatchTimeOverallPoint>;
}) => {
  if (!data?.length) return null;
  // The backend already returns cumulative `viewersRemaining` in 5-second
  // buckets, sorted ascending. We only trim the long tail where the
  // remaining-viewer count is below 2% of the peak (matches the previous UX
  // of dropping noisy tail buckets).
  const sorted = [...data].sort((a, b) => a.timeSeconds - b.timeSeconds);
  const total = sorted[0]?.viewersRemaining ?? 0;
  const isCut =
    sorted.length > 10 &&
    sorted[sorted.length - 1].viewersRemaining < total / 50;
  const trimmed = isCut
    ? sorted.filter((p) => p.viewersRemaining >= total / 50)
    : sorted;

  const showValues = trimmed.map((p) => ({
    x: p.timeSeconds,
    y: p.viewersRemaining,
  }));

  return (
    <ResponsiveLine
      data={[{ id: "Overall Video Viewing Time", data: showValues }]}
      pointColor={"#ff3666"}
      enableArea={true}
      curve={"monotoneX"}
      yScale={{ type: "linear", min: 0, max: "auto" }}
      axisBottom={{
        legend: "",
        tickValues: 3,
        tickRotation: -45,
        format: (d) => {
          const isEven = (parseInt(d) / 5) % 2 === 0;
          const isLast = d === showValues[showValues.length - 1].x;
          if (isLast && isCut) return d + "s+";
          return showValues.length > 20 ? (isEven ? d + "s" : "") : d + "s";
        },
      }}
      xFormat={(d) => `${d.toString()}s`}
      axisLeft={{
        legend: "",
        legendOffset: 12,
        format: (e) => Math.floor(e) === e && numberFormatter.format(e),
      }}
      margin={{
        left: 60,
        right: 30,
        top: 30,
        bottom: 45,
      }}
      motionConfig="gentle"
      useMesh={true}
      colors={["#ff366669"]}
      fill={[{ match: "*", id: "gradientC" }]}
      theme={{
        background: "#F7F8FA",
        axis: { domain: { line: { stroke: "gray", strokeWidth: 1 } } },
        textColor: theme.blings_teal,
        grid: { line: { stroke: theme.blings_teal_80, strokeWidth: 0.1 } },
      }}
      tooltip={TooltipLineNoFormat}
    />
  );
};
